import {
  full,
  half,
  INSIGHTS_LAYOUT,
  INSIGHTS_NON_SOCIALMEDIA_LAYOUT,
  oneThird,
  twoThirds,
} from "@components/WidgetGrid";

const GRID_COLUMNS = full; // for backwards compatibility

const getLayoutForComparison = (widgetCount, hasPrValueAndReachWidgets) => {
  const filler = half,
    layout = hasPrValueAndReachWidgets
      ? [
          // NOTE: compact widgets are not a part of the layout
          twoThirds,
          oneThird,
          half,
          half,
          oneThird,
          twoThirds,
          twoThirds,
          oneThird,
          half,
          half,
        ]
      : [twoThirds, oneThird];

  if (widgetCount < layout.length) {
    return layout.slice(0, widgetCount);
  } else {
    const paddingCount = widgetCount - layout.length,
      padding = Array.from(Array(paddingCount)).map(() => filler),
      padded = layout.concat(padding);

    return padded;
  }
};

const getLayoutForStandard = (widgetCount) => {
  const filler = half,
    // NOTE: compact widgets are not a part of the layout
    widths = [
      // 1 (number of widgets in the report)
      [full],
      // 2
      [full, full],
      // 3
      [full, half, half],
      // 4
      [full, oneThird, oneThird, oneThird],
      // 5
      [full, half, half, half, half],
      // 6+
      [full, oneThird, oneThird, oneThird, full, half],
    ];

  if (widgetCount <= 6) {
    return widths[widgetCount - 1];
  } else {
    const paddingCount = widgetCount - widths.length,
      padding = Array.from(Array(paddingCount)).map(() => filler),
      padded = widths[widths.length - 1].concat(padding);
    return padded;
  }
};

const getLayoutForSocialMediaOverview = (widgetCount) => {
  const filler = half,
    // NOTE: compact widgets are not a part of the layout
    widths = [
      // 1 (number of widgets in the report)
      [full],
      // 2
      [full, full],
      // 3
      [full, half, half],
      // 4
      [full, half, half, half],
      // 5
      [full, half, half, half, half],
      // 6+
      [full, half, half, full, half, half],
    ];

  if (widgetCount <= 6) {
    return widths[widgetCount - 1];
  } else {
    const paddingCount = widgetCount - widths.length,
      padding = Array.from(Array(paddingCount)).map(() => filler),
      padded = widths[widths.length - 1].concat(padding);
    return padded;
  }
};

const getLayout = (template, widgetCount) => {
  if ((template || "").indexOf("comparison") === 0) {
    return getLayoutForComparison(
      widgetCount,
      template === "comparison_with_reach"
    );
  } else if ((template || "").indexOf("social_media_overview") === 0) {
    return getLayoutForSocialMediaOverview(widgetCount);
  } else if ((template || "").indexOf("insights-social") === 0) {
    return INSIGHTS_LAYOUT;
  } else if ((template || "").indexOf("insights") === 0) {
    return INSIGHTS_NON_SOCIALMEDIA_LAYOUT;
  }
  return getLayoutForStandard(widgetCount);
};

export const getWidgetSize = (
  template,
  totalWidgetCount,
  currentWidgetIndex
) => {
  // Time comparison layout is currently always just `half`
  if (template === "time_comparison") return half;

  const layout = getLayout(template, totalWidgetCount);
  return layout[currentWidgetIndex];
};

const getCompactWidgetSize = (compactTotal, current, templateName) => {
  if (templateName === "time_comparison") return half;

  if (compactTotal === 5) {
    return GRID_COLUMNS / (current < 2 ? 2 : 3);
  } else if (compactTotal === 6) {
    return GRID_COLUMNS / (current < 2 ? 2 : 4);
  } else {
    return GRID_COLUMNS / Math.min(4, compactTotal);
  }
};

export const autoLayout = (templateName, unsortedWidgets) => {
  // first sort
  const sortedWidgets = unsortedWidgets.sort(
    (a, b) => a.sorting_position - b.sorting_position
  );

  // then find out if there are any compact widgets in the first 3
  let widgetCount = sortedWidgets.length;
  let topCompactWidgetsCount = 0;

  for (let i = 0; i < widgetCount; i++) {
    if (sortedWidgets[i].is_compact) topCompactWidgetsCount++;
    else break;
  }

  // if any, remove them from the total count so the layout handler
  // can use the first non-compact widget as start for the layout
  widgetCount -= topCompactWidgetsCount;

  // return an array with the widget and the layout props
  return sortedWidgets.map((widget, i) => {
    const size = widget.is_compact
      ? getCompactWidgetSize(topCompactWidgetsCount, i, templateName)
      : getWidgetSize(templateName, widgetCount, i - topCompactWidgetsCount);

    widget.size = size;

    // Override if mentions widget, which always take full row.
    // This should probably be saved in model to allow for further
    // customization, such as multiple row widgets.
    if (widget.metric === "mentions") {
      widget.size = full;
    }

    // HACK: Check if pop score widget should get full size
    const nextWidget = i < sortedWidgets.length - 1 && sortedWidgets[i + 1];

    if (
      widget.metric === "popularity_score" &&
      nextWidget &&
      nextWidget.metric === "mentions"
    ) {
      // count all previous widget's sizes to find out how much space they'll take
      let totalSize = 0;
      for (let j = 0; j < i; j++) {
        totalSize += sortedWidgets[j].size;
      }

      // popscore will end up alone on that row so give it full width
      if (totalSize % GRID_COLUMNS === 0) {
        widget.size = full;
      }
    }

    return widget;
  });
};
